/* AdminTeamPage — Upload foto dipendenti.
 * FASE 4.2.10 TASK C: drag&drop area + lista 13 dipendenti.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Upload, Trash2, Check, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const fileInputRef = useRef(null);

  const adminToken = () => {
    try { return JSON.parse(localStorage.getItem("colucci_admin_session") || "{}").token || ""; }
    catch (_e) { return ""; }
  };

  const loadTeam = () => {
    setLoading(true);
    axios.get(`${API}/admin/team/list`, { headers: { Authorization: `Bearer ${adminToken()}` } })
      .then((r) => setTeam(r.data?.items || []))
      .catch((err) => toast.error("Errore caricamento team: " + (err.response?.data?.detail?.message || err.message)))
      .finally(() => setLoading(false));
  };
  useEffect(loadTeam, []);

  const onUpload = async (slug, file) => {
    if (!file) return;
    setUploadingSlug(slug);
    const fd = new FormData();
    fd.append("slug", slug);
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/team/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken()}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Foto caricata e ottimizzata (${r.data.size_kb}KB · ${r.data.dimensions})`);
      loadTeam();
      // Invalida cache localStorage pubblica
      localStorage.removeItem("team_photos_v1");
    } catch (err) {
      toast.error("Errore upload: " + (err.response?.data?.detail?.message || err.message));
    } finally {
      setUploadingSlug(null);
    }
  };

  const onDelete = async (slug) => {
    if (!confirm(`Rimuovere la foto di ${slug}?`)) return;
    try {
      await axios.delete(`${API}/admin/team/photo/${slug}`, {
        headers: { Authorization: `Bearer ${adminToken()}` },
      });
      toast.success("Foto rimossa");
      loadTeam();
      localStorage.removeItem("team_photos_v1");
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail?.message || err.message));
    }
  };

  const photoCount = team.filter((m) => m.has_photo).length;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-team-page">
      <header className="mb-8">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">
          CRM · Gestione Sito
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#163321]">
          Foto dipendenti
        </h1>
        <p className="text-sm text-[#4A5E51] mt-2 max-w-2xl">
          Carica le foto dei membri del team. Vengono ottimizzate automaticamente a 600×750 WebP e
          pubblicate nella sezione team della homepage.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#163321]/5 rounded-full">
          <span className="text-xs font-semibold text-[#163321]">{photoCount}/{team.length}</span>
          <span className="text-xs text-[#4A5E51]">foto caricate</span>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-[#4A5E51]">Caricamento…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="team-grid">
          {team.map((m) => (
            <div key={m.slug} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`team-card-${m.slug}`}>
              <div className="aspect-[4/5] bg-slate-100 relative overflow-hidden">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={`${m.nome} ${m.cognome}`} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#163321] to-[#264A38] text-white">
                    <User className="w-16 h-16 opacity-40" />
                  </div>
                )}
                {m.has_photo && (
                  <div className="absolute top-2 right-2 bg-emerald-500 text-white rounded-full p-1.5 shadow">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-heading font-bold text-[#163321] leading-tight">{m.nome} {m.cognome}</h3>
                <p className="text-xs text-[#4A5E51] mt-0.5">{m.ruolo}</p>
                <p className="text-[10px] text-[#4A5E51]/80 mt-0.5 uppercase tracking-wider">{m.qualifica}</p>
                <div className="flex gap-2 mt-3">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => onUpload(m.slug, e.target.files?.[0])}
                      data-testid={`team-upload-${m.slug}`}
                      disabled={uploadingSlug === m.slug}
                    />
                    <span className={`block w-full cursor-pointer text-center px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${uploadingSlug === m.slug ? "bg-slate-200 text-slate-500" : "bg-[#163321] text-white hover:bg-[#1F4030]"}`}>
                      {uploadingSlug === m.slug ? "Caricamento…" : (<><Upload className="w-3 h-3 inline mr-1" /> {m.has_photo ? "Sostituisci" : "Carica"}</>)}
                    </span>
                  </label>
                  {m.has_photo && (
                    <button
                      onClick={() => onDelete(m.slug)}
                      className="px-3 py-2 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                      data-testid={`team-delete-${m.slug}`}
                      aria-label="Rimuovi foto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
